import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';
import { useFieldControl } from './Field.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SWITCH — AN ON/OFF SETTING THAT TAKES EFFECT AT ONCE. Follows the five rules in `Button.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════
 * Do not disturb (session 19, design §10.2) was the first setting here that is saved the moment
 * it is pressed — no form, no Save. A checkbox says "this will be part of what I submit"; a
 * switch says "this is now on". So it is `role="switch"` with `aria-checked`, which a screen
 * reader reads as "Do not disturb, switch, on" — and the state is in that attribute and nowhere
 * else: there is no `checked` styling that the accessibility tree could disagree with.
 *
 * ── A `<button>`, NOT A STYLED CHECKBOX ─────────────────────────────────────
 * `role="switch"` on a native button gets Space AND Enter, focus and the disabled state from the
 * browser, with no key handler written here (APG "Switch", button pattern). A hidden checkbox
 * under a painted track is the usual build, and it splits the control in two: the thing that has
 * focus is not the thing that is drawn.
 *
 * ── THE STATE IS THE THUMB'S POSITION, NOT ITS COLOUR (WCAG 1.4.1) ──────────
 * Off: the thumb at the start, on a sunken track with the strong border (3:1 against both
 * surfaces, tokens.css). On: at the end, on the accent. Colour emphasises; position carries it —
 * and in forced-colors mode, where both fills are flattened, the border and the thumb survive.
 *
 * ── THE ONE MOTION: THE THUMB, 150 ms ───────────────────────────────────────
 * State indication — the eye follows the thumb to where the state went. `transform` only (no
 * layout), `ease-move` (the strong ease-in-out for on-screen movement, styles.css), and the
 * console's 150 ms. A transition rather than keyframes, so a second press mid-way reverses from
 * where the thumb is. `styles.css` collapses every transition under `prefers-reduced-motion`.
 *
 * ── `loading` IS `aria-busy`, AS ON `Button` ────────────────────────────────
 * While a change is being saved the switch stays focusable and SAYS it is busy, and a press is
 * ignored — `disabled` would drop focus to `<body>` under a keyboard user mid-press.
 *
 * Inside a `Field` it is named by the field's label and described by its description and
 * error, like every control here (`useFieldControl`); outside one, the caller names it.
 */

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value' | 'role' | 'type' | 'aria-checked' | 'children'> {
  checked: boolean;
  onCheckedChange(next: boolean): void;
  /** Saving: announced as busy, presses ignored, focus kept. */
  loading?: boolean;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, loading = false, className, disabled, id, onClick, ...rest },
  ref,
) {
  const field = useFieldControl();
  const describedBy = rest['aria-describedby'] ?? field?.['aria-describedby'];

  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      id={id ?? field?.id}
      // The Field's label names it — `aria-labelledby` rather than relying on `<label for>`
      // reaching a button, which accessible-name implementations do not all honour.
      {...(field && rest['aria-label'] === undefined && rest['aria-labelledby'] === undefined ? { 'aria-labelledby': field.labelId } : {})}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      {...(field?.['aria-invalid'] ? { 'aria-invalid': true } : {})}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || loading) return;
        onCheckedChange(!checked);
      }}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-full border p-0.5',
        'transition-colors duration-150',
        checked ? 'border-accent bg-accent hover:border-accent-hover hover:bg-accent-hover' : 'border-border-strong bg-surface-sunken hover:border-fg-muted',
        'disabled:pointer-events-none disabled:opacity-50',
        'aria-busy:cursor-progress aria-busy:opacity-70',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'block size-4.5 rounded-full shadow-sm transition-transform duration-150 ease-move',
          checked ? 'translate-x-5 bg-accent-fg' : 'translate-x-0 bg-fg-muted',
        )}
      />
    </button>
  );
});
